const INTERNAL_ERROR_TERMS = [
  'violates check constraint',
  'relation',
  'transactions_transaction_type_check',
  'sql',
  'postgres',
  'supabase',
  'new row',
  'null value',
  'foreign key',
];

const HUMAN_LABELS = {
  redemption: 'Coin redemption',
  refund: 'Refund',
  purchase: 'Purchase',
  manual: 'Manual access',
  coin_unlock: 'Coin unlock',
  coins: 'Coin unlock',
  stripe: 'Card payment',
  one_time: 'One-time access',
  subscription: 'Subscription',
  trial: 'Trial',
  trialing: 'Trial',
  active: 'Active',
  succeeded: 'Paid',
  expired: 'Expired',
  pending: 'Pending',
  failed: 'Failed',
  cancelled: 'Cancelled',
  canceled: 'Cancelled',
  refunded: 'Refunded',
};

const TITLE_BY_TYPE = {
  redemption: 'Coin redemption',
  refund: 'Refund',
  purchase: 'Purchase',
  manual: 'Manual access',
  coin_unlock: 'Coin unlock',
  stripe: 'Card payment',
  one_time: 'One-time access',
  subscription: 'Subscription',
  trial: 'Trial',
  Merchandise: 'Merchandise',
  Challenge: 'Challenge',
  Tournament: 'Tournament',
  'Tournament Enrollment': 'Tournament enrollment',
  'Tournament Creation': 'Tournament creation',
  Booking: 'Booking',
  'Scheduled Session': 'Scheduled session',
  'ProAm Store': 'ProAm Store',
  'Event Registration': 'Event registration',
  'Event Listing Fee': 'Event listing fee',
  'ProAm Venue': 'ProAm venue',
  'Workout Groups': 'Workout Groups',
};

const asNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const asOptionalNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const humanizeToken = (value, fallback = 'Transaction') => {
  const raw = String(value || '').trim();
  if (!raw) return fallback;
  const key = raw.toLowerCase();
  if (HUMAN_LABELS[key]) return HUMAN_LABELS[key];
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

export const formatMoney = (amount, currency = 'eur') => {
  const numeric = Number(amount);
  if (!Number.isFinite(numeric)) return '';
  if (Object.is(numeric, -0) || numeric === 0) return 'Free';
  return Math.abs(numeric).toLocaleString('en-IE', {
    style: 'currency',
    currency: String(currency || 'eur').toUpperCase(),
  });
};

export const formatCoins = (amount) => `${asNumber(amount).toLocaleString('en-IE')} coins`;

const storeOrderItems = (order = {}) => {
  if (!order || typeof order !== 'object') return [];
  return Array.isArray(order.order_items) ? order.order_items : Array.isArray(order.items) ? order.items : [];
};

const storeOrderOriginalTotal = (order = {}) => {
  const itemsTotal = storeOrderItems(order).reduce((total, item) => (
    total + (asNumber(item.price) * Math.max(1, asNumber(item.quantity) || 1))
  ), 0);
  return itemsTotal + asNumber(order.delivery_fee);
};

export const getTransactionStoreOrder = (transaction = {}) => transaction.store_order || transaction.order || null;

export const getStoreOrderPrimaryItem = (transaction = {}) => {
  const order = getTransactionStoreOrder(transaction);
  return storeOrderItems(order)[0] || null;
};

export const getTransactionDiscount = (transaction = {}) => {
  const metadata = transaction.discount_metadata || {};
  const order = getTransactionStoreOrder(transaction);
  const orderCode = order?.discount || order?.discount_code || '';
  const code = transaction.discount_code || metadata.code || metadata.discount_code || orderCode || '';
  const discountCoins = asOptionalNumber(transaction.discount_coins ?? metadata.discount_coins ?? metadata.saved_coins);
  const finalCoins = asOptionalNumber(metadata.final_coins ?? metadata.charged_coins ?? transaction.coins_amount ?? transaction.proam_coins);
  const originalCoins = asOptionalNumber(metadata.original_coins ?? (
    discountCoins !== null && finalCoins !== null ? discountCoins + Math.abs(finalCoins) : null
  ));
  const discountAmountCents = asOptionalNumber(transaction.discount_amount_cents ?? metadata.discount_amount_cents);
  const finalAmountCents = asOptionalNumber(metadata.final_amount_cents ?? transaction.amount_cents);
  const originalAmountCents = asOptionalNumber(metadata.original_amount_cents ?? (
    discountAmountCents !== null && finalAmountCents !== null ? discountAmountCents + finalAmountCents : null
  ));

  if (order) {
    const finalOrderTotal = asNumber(order.total_price);
    const originalOrderTotal = storeOrderOriginalTotal(order);
    const savedOrderTotal = Math.max(0, originalOrderTotal - finalOrderTotal);
    if (orderCode || savedOrderTotal > 0) {
      return {
        code: code || orderCode,
        originalLabel: formatMoney(originalOrderTotal, transaction.currency || 'eur'),
        discountLabel: savedOrderTotal > 0 ? formatMoney(savedOrderTotal, transaction.currency || 'eur') : '',
        finalLabel: formatMoney(finalOrderTotal, transaction.currency || 'eur'),
      };
    }
  }

  if (!code && !discountCoins && !discountAmountCents) return null;

  return {
    code,
    originalLabel: originalCoins && originalCoins > 0
      ? formatCoins(originalCoins)
      : originalAmountCents && originalAmountCents > 0
      ? formatMoney(originalAmountCents / 100, transaction.currency || 'eur')
      : '',
    discountLabel: discountCoins && discountCoins > 0
      ? formatCoins(discountCoins)
      : discountAmountCents && discountAmountCents > 0
      ? formatMoney(discountAmountCents / 100, transaction.currency || 'eur')
      : '',
    finalLabel: finalCoins !== null && finalCoins >= 0
      ? formatCoins(Math.abs(finalCoins))
      : finalAmountCents !== null && finalAmountCents >= 0
      ? formatMoney(finalAmountCents / 100, transaction.currency || 'eur')
      : '',
  };
};

export const formatTransactionDiscountSummary = (transaction = {}) => {
  const discount = getTransactionDiscount(transaction);
  if (!discount) return '';
  const parts = [];
  if (discount.code) parts.push(`Discount ${discount.code}`);
  if (discount.discountLabel) parts.push(`saved ${discount.discountLabel}`);
  return parts.join(' · ');
};

export const formatTransactionDate = (date) => {
  if (!date) return 'Date unavailable';
  const parsed = new Date(date);
  if (Number.isNaN(parsed.getTime())) return 'Date unavailable';
  return parsed.toLocaleDateString('en-IE', { day: 'numeric', month: 'short', year: 'numeric' });
};

export const isInternalErrorText = (text) => {
  const lower = String(text || '').toLowerCase();
  return INTERNAL_ERROR_TERMS.some((term) => lower.includes(term));
};

export const sanitizeTransactionDescription = (text) => {
  if (!text) return '';
  if (isInternalErrorText(text)) return 'Transaction issue - contact support';
  return String(text).trim();
};

export const isTransactionIssue = (transaction = {}) => (
  isInternalErrorText(transaction.description) ||
  isInternalErrorText(transaction.reason) ||
  isInternalErrorText(transaction.error) ||
  isInternalErrorText(transaction.message)
);

export const formatTransactionTitle = (transaction = {}) => {
  if (isTransactionIssue(transaction)) return 'Transaction issue - contact support';
  const description = sanitizeTransactionDescription(transaction.description || transaction.reason || '');
  if (description) return description;
  const type = transaction.transaction_type || transaction.type || transaction.source;
  return TITLE_BY_TYPE[type] || humanizeToken(type, 'Transaction');
};

export const formatTransactionStatus = (transaction = {}) => {
  if (isTransactionIssue(transaction)) return 'Issue';
  const status = String(transaction.status || '').toLowerCase();
  const source = String(transaction.source || '').toLowerCase();
  if (status === 'succeeded' && source === 'purchase') return 'Paid';
  return HUMAN_LABELS[status] || HUMAN_LABELS[source] || humanizeToken(status || source, 'Recorded');
};

export const formatTransactionAmount = (transaction = {}) => {
  if (typeof transaction.amount_cents === 'number') {
    if (transaction.amount_cents === 0) return transaction.status === 'refunded' ? 'Refunded' : 'Free';
    return formatMoney(transaction.amount_cents / 100, transaction.currency || 'eur');
  }
  if (typeof transaction.amount !== 'undefined') {
    const amount = asNumber(transaction.amount);
    if (amount === 0) return transaction.status === 'refunded' ? 'Refunded' : 'Free';
    if (String(transaction.source || '').toLowerCase() === 'refund' || String(transaction.status || '').toLowerCase() === 'refunded') {
      return `Refunded ${formatMoney(amount, transaction.currency || 'eur')}`;
    }
    return formatMoney(amount, transaction.currency || 'eur');
  }
  if (typeof transaction.coins_amount === 'number') return formatCoins(transaction.coins_amount);
  if (typeof transaction.proam_coins === 'number') return formatCoins(Math.abs(transaction.proam_coins));
  if (typeof transaction.delta === 'number') return `${transaction.delta > 0 ? '+' : ''}${formatCoins(transaction.delta)}`;
  return 'Recorded';
};

export const getTransactionIcon = (transaction = {}) => {
  if (isTransactionIssue(transaction)) return 'issue';
  const haystack = `${transaction.transaction_type || ''} ${transaction.type || ''} ${transaction.source || ''}`.toLowerCase();
  if (haystack.includes('refund')) return 'refund';
  if (haystack.includes('coin') || haystack.includes('redemption')) return 'coins';
  if (haystack.includes('tournament') || haystack.includes('challenge')) return 'trophy';
  if (haystack.includes('workout')) return 'workout';
  if (haystack.includes('booking') || haystack.includes('session')) return 'calendar';
  if (haystack.includes('stripe') || haystack.includes('purchase') || haystack.includes('store')) return 'card';
  return 'receipt';
};

export const getTransactionTone = (transaction = {}) => {
  if (isTransactionIssue(transaction)) return 'issue';
  const status = String(transaction.status || '').toLowerCase();
  const source = String(transaction.source || '').toLowerCase();
  if (status === 'failed' || status === 'cancelled' || status === 'canceled') return 'danger';
  if (status === 'pending') return 'warning';
  if (status === 'refunded' || source === 'refund') return 'success';
  if (source === 'redemption' || asNumber(transaction.proam_coins) < 0 || asNumber(transaction.delta) < 0) return 'neutral';
  return 'default';
};

export const getPrimaryTransactionAction = (transaction = {}) => {
  if (isTransactionIssue(transaction)) return 'Contact support';
  if (String(transaction.status || '').toLowerCase() === 'failed') return 'Get support';
  return 'View details';
};

export const shortenTransactionId = (id) => {
  const raw = String(id || '').replace(/-/g, '');
  if (!raw) return 'Unavailable';
  return raw.length <= 8 ? raw.toUpperCase() : raw.slice(-8).toUpperCase();
};
